/**
 * Standardized Result Types
 * Provides consistent error handling patterns across the application
 */

/**
 * Success result
 */
export interface Success<T = void> {
  success: true;
  data: T;
}

/**
 * Error result
 */
export interface Failure {
  success: false;
  error: string;
  errorCode?: string;
  details?: unknown;
}

/**
 * Result type - either success or failure
 * Inspired by Rust's Result<T, E> pattern
 */
export type Result<T = void> = Success<T> | Failure;

/**
 * Create a success result
 */
export const success = <T = void>(data: T): Success<T> => ({
  success: true,
  data,
});

/**
 * Create a success result without data
 */
export const successVoid = (): Success<void> => ({
  success: true,
  data: undefined,
});

/**
 * Create a failure result
 */
export const failure = (error: string, errorCode?: string, details?: unknown): Failure => ({
  success: false,
  error,
  errorCode,
  details,
});

/**
 * Type guard to check if result is successful
 */
export const isSuccess = <T>(result: Result<T>): result is Success<T> => {
  return result.success === true;
};

/**
 * Type guard to check if result is a failure
 */
export const isFailure = (result: Result<unknown>): result is Failure => {
  return result.success === false;
};

/**
 * Unwrap a result, throwing if it's an error
 */
export const unwrap = <T>(result: Result<T>): T => {
  if (isSuccess(result)) {
    return result.data;
  }
  throw new Error(result.error);
};

/**
 * Get data from result or return default value
 */
export const unwrapOr = <T>(result: Result<T>, defaultValue: T): T => {
  if (isSuccess(result)) {
    return result.data;
  }
  return defaultValue;
};

/**
 * Convert error to Result type
 */
export const fromError = (error: unknown): Failure => {
  if (error instanceof Error) {
    return failure(error.message);
  }
  if (typeof error === 'string') {
    return failure(error);
  }
  return failure('An unknown error occurred', undefined, error);
};

/**
 * Wrap an async function to return Result instead of throwing
 */
export const tryCatch = async <T>(
  fn: () => Promise<T>
): Promise<Result<T>> => {
  try {
    const data = await fn();
    return success(data);
  } catch (error) {
    return fromError(error);
  }
};

/**
 * Wrap a sync function to return Result instead of throwing
 */
export const tryCatchSync = <T>(fn: () => T): Result<T> => {
  try {
    const data = fn();
    return success(data);
  } catch (error) {
    return fromError(error);
  }
};
